import { sdkLogger } from './logger';
import { SdkApi, SdkError, SdkType } from '../interface';
import { SdkResponseCode } from '../constant';

/**
 * SDK调用上下文
 */
interface SdkContext {
    // SDK调用结果
    result: any;
    // SDK类型
    sdkType: SdkType;
    // SDK API
    sdkApi: SdkApi;
    // 日志前缀
    prefix?: string;
}

/**
 * 设置超时时间，如果超过 60 秒，则记录超时日志
 * 60 秒的超时时间没有明确参考，但基本上网络正常情况下，60 秒内绝大部分 API 调用应该都会返回结果
 */
const SDK_API_TIME_LIMIT = 60_000;

/**
 * 捕获SDK调用结果的公共函数
 * @param sdkContext SDK调用上下文
 * @returns 处理后的结果
 */
export const captureSdkResult = (sdkContext: SdkContext) => {
    const { result, sdkType, sdkApi, prefix = '' } = sdkContext;
    const logPrefix = `${sdkType} ${sdkApi} ${prefix}`;
    if (result instanceof Promise) {
        return new Promise((resolve, reject) => {
            /**
             * 在某些场景下，调用jssdk API时，可能既不会走到 onSuccess 回调，也不会走到 onFail 回调，
             * 此时给 API 包裹的 promise 就会一直是 pending 状态，从而无法获取成功或失败的结果。
             *
             * 经过和测试讨论，目前超时概率较大的的是图片上传（单张最大 5MB * 最多9张），加上用户设备差异性和网络状况等原因
             * 超时时限其实不好控制，且目前超时后阻塞用户的情况比较少，而图片上传场景使用概率大得多，所以综合考虑下采取以下方案：
             * 超时后，仅记录超时日志，而不终止异步任务
             * @see FXD-72288
             */
            const timer = setTimeout(() => {
                const timeoutErrMsg = `${logPrefix} get no result after ${SDK_API_TIME_LIMIT} ms`;
                sdkLogger.warn(timeoutErrMsg);
                clearTimeout(timer);
            }, SDK_API_TIME_LIMIT);
            result
                .then((res) => {
                    sdkLogger.info(`${logPrefix} success`, { res });
                    resolve(res);
                })
                .catch((err) => {
                    sdkLogger.error(`${logPrefix} fail`, { err });
                    reject(err);
                })
                .finally(() => {
                    clearTimeout(timer);
                });
        });
    }
    sdkLogger.info(`${logPrefix} result not promise`, { res: result });
    return result;
};

interface SdkTimeoutContext<T> {
    result: Promise<T>;
    // SDK类型
    sdkType: SdkType;
    // SDK API
    sdkApi: SdkApi;
    // 超时时间
    timeout?: number;
}

/**
 * 设置超时兜底，解决 SDK 可能一直 pending 不回调的问题
 */
export const withSdkTimeout = <T>(sdkContext: SdkTimeoutContext<T>) => {
    const { result, sdkType, sdkApi, timeout = 3000 } = sdkContext;
    return new Promise<T>((resolve, reject) => {
        let settled = false;

        const finish = (settle: () => void) => {
            if (settled) {
                return;
            }
            settled = true;
            clearTimeout(timer);
            settle();
        };

        const timer = setTimeout(() => {
            finish(() => {
                reject(
                    new SdkError(
                        sdkType,
                        SdkResponseCode.ThirdPartyServiceFail,
                        `${sdkApi} timeout`
                    )
                );
            });
        }, timeout);

        result.then(
            (res) => {
                finish(() => {
                    resolve(res);
                });
            },
            (err) => {
                finish(() => {
                    reject(err);
                });
            }
        );
    });
};
